'use client'

import { useState, useEffect, useRef, useCallback, Fragment, type RefObject } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { usePathname } from 'next/navigation'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { easeMedium } from '@/lib/transitions'
import { useScrollGradients } from '@/hooks/useScrollGradients'
import ScrollView from '@/components/ui/ScrollView'
import { usePageContext } from '@/components/content/common/PageContext'
import type { App, Category } from '@/payload-types'

type CategoryFilter = Pick<Category, 'id' | 'name' | 'slug' | 'icon'>

type FilterSelectProps = {
  filters: CategoryFilter[]
}

type PageSelectListProps = {
  options: CategoryFilter[]
  sidebar?: boolean
  applySelection: (next: CategoryFilter[]) => void
}

type PageSelectItemProps = {
  option: CategoryFilter
  selected: boolean
  focus?: boolean
  onToggleAction?: () => void
}

type PageSelectSearchProps = {
  setSearchAction: (search: string) => void
  autoFocus?: boolean
}

export default function FilterSelect({ filters }: FilterSelectProps) {
  const [open, setOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const { selectedCategories, setSelectedCategories } = usePageContext()
  const path = usePathname()

  // Runs the actual selection update a frame later so the loader icon has a
  // chance to paint before the (synchronous, potentially heavy) list re-render.
  const applySelection = useCallback(
    (next: CategoryFilter[]) => {
      setIsProcessing(true)
      requestAnimationFrame(() => {
        setSelectedCategories(next)
        requestAnimationFrame(() => setIsProcessing(false))
      })
    },
    [setSelectedCategories],
  )

  useEffect(() => {
    const pathSlug = path.split('/').pop()

    const option = filters.find((c) => {
      const item = c as Category | App
      return item.slug === pathSlug
    })

    if (!option) return
    setSelectedCategories([option])
  }, [path, filters])

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [open])

  const hasSelection = selectedCategories && selectedCategories?.length > 0

  return (
    <>
      <div className="flex min-w-0 items-center gap-2">
        <div className="relative z-50 shrink-0">
          <div className="flex">
            <div
              onClick={() => setOpen(!open)}
              className={cn(
                'flex h-9 cursor-pointer items-center gap-2.5 rounded-lg border border-black/10 bg-gray-50 px-3 transition-all hover:border-primary-500/80',
                open && 'border-primary-500/80',
                hasSelection && 'rounded-r-none',
              )}
            >
              <span className="hidden items-center gap-2.5 xl:flex">
                <Icon name="bars-filter" size={12} />
                <span className="text-14 font-medium text-gray-800">Filter</span>
              </span>
              <span className="flex items-center gap-2.5 xl:hidden">
                {!hasSelection && <Icon name="bars-filter" size={12} />}
                <span className="line-clamp-1 shrink text-14 font-medium text-gray-800">
                  {hasSelection
                    ? selectedCategories
                        .slice(0, 2)
                        .map((c) => c.name)
                        .join(', ') +
                      (selectedCategories.length > 2 ? `, ${selectedCategories.length - 2}+` : '')
                    : 'Filter'}
                </span>
              </span>
              <div
                className={cn(
                  '-rotate-90 text-gray-400 transition-transform ease-in-out',
                  open && 'rotate-90',
                  isProcessing && 'animate-spin',
                )}
              >
                <Icon name={isProcessing ? 'loader' : 'chevron-left'} size={12} />
              </div>
            </div>

            {hasSelection && (
              <div
                onClick={() => applySelection([])}
                className="flex h-9 cursor-pointer items-center justify-center rounded-r-lg border-y border-r border-black/10 bg-gray-50 px-2 text-gray-400 transition-colors hover:bg-gray-100"
              >
                <Icon name="xmark-small" size={12} />
              </div>
            )}
          </div>

          <AnimatePresence>
            {open && <FilterSelectList options={filters} applySelection={applySelection} />}
          </AnimatePresence>
        </div>

        {hasSelection && (
          <div className="hidden min-w-0 xl:block">
            <ScrollView className="flex items-center gap-1.5 py-0.5">
              {selectedCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() =>
                    applySelection(selectedCategories.filter((c) => c.id !== category.id))
                  }
                  aria-label={`Remove ${category.name} filter`}
                  className="flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-black/5 pl-2.5 pr-3.5 text-14 font-medium text-zinc-700 transition-colors hover:bg-black/10"
                >
                  <Icon
                    name="xmark-small"
                    size={12}
                    className="text-zinc-400 transition-colors group-hover:text-zinc-600"
                  />
                  {category.name}
                </button>
              ))}
            </ScrollView>
          </div>
        )}
      </div>

      {open && (
        <div onClick={() => setOpen(false)} className="fixed left-0 top-0 z-40 h-full w-full"></div>
      )}
    </>
  )
}

export function FilterSelectList({ options, applySelection }: PageSelectListProps) {
  const { selectedCategories } = usePageContext()
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [initialSelectedIds] = useState(() => selectedCategories?.map((c) => c.id) ?? [])

  const toggleCategory = useCallback(
    (option: CategoryFilter) => {
      const isSelected = selectedCategories?.some((c) => c.id === option.id) ?? false
      const next = isSelected
        ? selectedCategories?.filter((c) => c.id !== option.id) ?? []
        : [...(selectedCategories ?? []), option as CategoryFilter]
      applySelection(next)
    },
    [selectedCategories, applySelection],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const categoryRef = useRef<HTMLDivElement[]>([])

  const { showTopGradient, showBottomGradient } = useScrollGradients(
    containerRef as RefObject<HTMLElement>,
  )
  const ignoreHoverRef = useRef(false)

  const filteredOptions = options.filter((option) =>
    option.name?.toLowerCase().includes(search.toLowerCase()),
  )

  const initiallySelected = filteredOptions.filter((option) =>
    initialSelectedIds.includes(option.id),
  )
  const remaining = filteredOptions.filter((option) => !initialSelectedIds.includes(option.id))
  const orderedOptions = [...initiallySelected, ...remaining]
  const showDivider = initiallySelected.length > 0 && remaining.length > 0

  const filteredCount = orderedOptions.length

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement
      if (
        activeElement &&
        (activeElement.getAttribute('data-chat-input') === 'true' ||
          activeElement.closest('[data-chat-search]'))
      ) {
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        setSelectedIndex((prev) => Math.min(prev + 1, filteredCount - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        if (selectedIndex < 0 || selectedIndex >= filteredCount) return
        e.preventDefault()
        e.stopPropagation()
        toggleCategory(orderedOptions[selectedIndex])
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedIndex, filteredCount, orderedOptions, toggleCategory])

  useEffect(() => {
    if (selectedIndex >= 0) {
      categoryRef.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [selectedIndex])

  useEffect(() => {
    setSelectedIndex(0)
    ignoreHoverRef.current = true
  }, [search])

  useEffect(() => {
    const handleMouseMove = () => {
      if (ignoreHoverRef.current) {
        ignoreHoverRef.current = false
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0, marginTop: 10 }}
      animate={{ opacity: 1, marginTop: 0 }}
      exit={{ opacity: 0, marginTop: 10 }}
      transition={easeMedium}
      className={cn(
        // Base styles
        'hide-scrollbar fixed z-45 h-fit',
        'flex w-[calc(100vw-24px)] min-w-[280px] cursor-pointer flex-col items-center overflow-hidden',
        'border border-black/10 bg-white shadow-subtle',
        'before:absolute before:inset-0 before:-z-10 before:backdrop-blur',

        // Position variants
        'left-1/2 top-36 max-h-[410px] -translate-x-1/2 rounded-2xl md:absolute md:left-0 md:top-10 md:max-h-[430px] md:max-w-[320px] md:translate-x-0',
      )}
    >
      <FilterSelectSearch setSearchAction={setSearch} autoFocus={true} />
      <div
        className={cn(
          'relative flex h-full w-full flex-col overflow-hidden',
          'before:pointer-events-none before:absolute before:left-0 before:top-0 before:z-10 before:h-8 before:w-full before:bg-gradient-to-b before:from-white before:to-transparent before:opacity-0 before:transition-opacity before:content-[""]',
          'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:z-10 after:h-8 after:w-full after:bg-gradient-to-t after:from-white after:to-transparent after:opacity-0 after:transition-opacity after:content-[""]',
          showTopGradient && 'before:opacity-100',
          showBottomGradient && 'after:opacity-100',
        )}
      >
        <div
          ref={containerRef}
          className="flex min-h-0 w-full flex-1 flex-col gap-2 overflow-y-auto p-3 md:gap-0.5"
        >
          {orderedOptions.length > 0 ? (
            orderedOptions.map((option, index) => {
              const focus = selectedIndex === index

              return (
                <Fragment key={option.id}>
                  {showDivider && index === initiallySelected.length && (
                    <div className="flex w-full items-center px-2 py-1.5">
                      <div className="h-px w-full shrink-0 bg-black/10" aria-hidden="true" />
                    </div>
                  )}
                  <div
                    onMouseEnter={() => {
                      if (ignoreHoverRef.current) return
                      setSelectedIndex(index)
                    }}
                    onMouseLeave={() => {
                      if (ignoreHoverRef.current) return
                      setSelectedIndex(-1)
                    }}
                    ref={(el) => {
                      categoryRef.current[index] = el as HTMLDivElement
                    }}
                  >
                    <FilterSelectItem
                      option={option}
                      selected={selectedCategories?.some((c) => c.id === option.id) ?? false}
                      focus={focus}
                      onToggleAction={() => toggleCategory(option)}
                    />
                  </div>
                </Fragment>
              )
            })
          ) : (
            <div className="flex h-7 w-full shrink-0 items-center px-1.5 text-13 font-medium">
              No results
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export function FilterSelectItem({ option, selected, focus, onToggleAction }: PageSelectItemProps) {
  return (
    <button
      role="checkbox"
      aria-checked={selected}
      onClick={onToggleAction}
      className={cn(
        'group flex h-11 w-full shrink-0 items-center gap-4 rounded-lg border border-white/0 pl-1.5 pr-1 text-13 text-gray-500 transition-colors duration-300 md:h-10',
        focus && 'bg-gray-50',
      )}
    >
      <div
        className={cn(
          'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-key border border-black/10 text-white transition-colors',
          selected && 'bg-primary-500',
        )}
      >
        <Icon
          name="check"
          size={12}
          className={cn('transition-opacity', selected ? 'opacity-100' : 'opacity-0')}
        />
      </div>

      <span className="w-full whitespace-nowrap text-left text-15 transition-colors duration-300 md:text-13">
        {option.name}
      </span>

      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center text-gray-500 opacity-0 transition-opacity',
          focus && 'opacity-100',
        )}
      >
        <Icon name="return-key" size={12} />
      </div>
    </button>
  )
}

export function FilterSelectSearch({ setSearchAction, autoFocus }: PageSelectSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus && window.innerWidth >= 640 && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [autoFocus])

  return (
    <div className="flex h-[50px] w-full shrink-0 items-center gap-3.5 border-b border-black/10 px-5 text-gray-800 md:h-12 md:gap-4">
      <Icon
        name="magnifier"
        size={14}
        className="flex h-7 w-7 shrink-0 items-center justify-center"
      />
      <input
        ref={inputRef}
        type="text"
        placeholder={`Search categories...`}
        className="h-full w-full bg-transparent text-16 placeholder:text-gray-400 focus:outline-none md:text-13"
        onChange={(e) => setSearchAction(e.target.value)}
      />
    </div>
  )
}
